import React from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { typography, spacing, colors } from "../styles/designSystem";
import { PageHeadline } from "../components/Typography";
import { primaryButtonBase } from "../styles/buttonStyles";

/**
 * Terminal 404.
 *
 * The site is a SPA behind a catch-all Netlify redirect, so every unmatched URL
 * still serves index.html. Without a wildcard route those requests rendered an
 * entirely blank page — no nav, no footer, no way back.
 */

const PageWrapper = styled.div`
    max-width: 720px;
    margin: 0 auto;
    padding: ${spacing[1100]} ${spacing[700]} ${spacing[1200]};
    text-align: left;

    @media (max-width: 768px) {
        padding: ${spacing[900]} ${spacing[400]} ${spacing[1000]};
    }
`;

const Path = styled.div`
    font-family: ${typography.fontFamily.primary};
    font-size: ${typography.fontSize[200]};
    color: ${({ theme }) => theme.subtleText};
    margin-bottom: ${spacing[600]};
    word-break: break-all;

    &::before {
        content: "> ACCESS ";
        font-family: ${typography.fontFamily.display};
        letter-spacing: ${typography.letterSpacing.wide};
        opacity: 0.7;
    }
`;

const ErrorRow = styled.div`
    margin-bottom: ${spacing[500]};
`;

const Error = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[200]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.widest};
    color: ${({ theme }) => theme.background};
    background: ${colors.amber[400]};
    display: inline-block;
    padding: ${spacing[100]} ${spacing[300]};
    text-shadow: none;
`;

const Body = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 ${spacing[800]};
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing[400]};
`;

const Action = styled(Link)`
    ${primaryButtonBase}
    padding: ${spacing[300]} ${spacing[600]};
`;

const NotFound = () => {
    const { pathname } = useLocation();

    return (
        <PageWrapper>
            <Path>{pathname}</Path>
            <ErrorRow>
                <Error>Error 404</Error>
            </ErrorRow>
            <PageHeadline>Record Not Found</PageHeadline>
            <Body>
                No entry at that address in this terminal&rsquo;s database. The record may have been
                moved or removed. Return to the main terminal, or open the employment record
                directly.
            </Body>
            <Actions>
                <Action to="/">Return to Terminal</Action>
                <Action to="/contact" color={colors.amber[400]}>
                    Open Channel
                </Action>
            </Actions>
        </PageWrapper>
    );
};

export default NotFound;
